// The record's shared contract: which tabs exist, what the header says, and the
// three small derivations neither surface should re-invent.
//
// The TABS are a URL segment (`/patients/:caseId/:tab?`), so this list is also a
// whitelist: a stale or hand-edited link must land on a real tab rather than
// render nothing. `overview` is the default and is omitted from the URL, which
// is what keeps every link to a patient short.
//
// Pure and React-free.

import {
  RECORD_TABS, DEFAULT_RECORD_TAB, resolveRecordTab, isRecordTab,
} from '../../constants/routes.js';

export { RECORD_TABS, DEFAULT_RECORD_TAB, resolveRecordTab };

/** Is this URL segment one of the four tabs? A stale one is corrected, not rendered. */
export const isRecordTabParam = (id) => isRecordTab(id);

export const TAB_LABEL_KEYS = {
  overview: 'record.tabs.overview',
  history: 'record.tabs.history',
  visits: 'record.tabs.visits',
  timeline: 'record.tabs.timeline',
};

/**
 * The chips beside the patient's name: age·sex, blood type, visit count, last
 * visit. Returned as data rather than nodes so both surfaces render them in
 * their own chrome, and so a test can read the header without a DOM.
 *
 * Empty facts are DROPPED, not rendered as «—»: a dash beside a name reads as a
 * value the student entered, and this header is the one place a patient's facts
 * are stated rather than edited.
 */
export function headerChips(row, { t, formatNumber, formatDate, ageLabel }) {
  if (!row) return [];
  const chips = [];
  const age = ageLabel(row, t);
  const sex = row.sex ? t(`common.gender.${row.sex}`) : '';
  const line = [age, sex].filter(Boolean).join(' · ');
  if (line) chips.push({ id: 'age', label: line });
  if (row.bloodType) chips.push({ id: 'blood', label: row.bloodType });
  chips.push({
    id: 'visits',
    label: t('record.header.visits', { count: formatNumber(Number(row.encounterCount || 0)) }),
  });
  if (row.lastEncounterAtMs) {
    chips.push({
      id: 'lastVisit',
      label: t('record.header.lastVisit', { date: formatDate(row.lastEncounterAtMs, 'numeric') }),
    });
  }
  return chips;
}

/**
 * «شروع ویزیت» or «ادامهٔ ویزیت باز» — ONE button, two labels.
 *
 * Same place, same weight, same colour: the student's intent («work on this
 * patient») does not change because a visit happens to be open, and a second
 * button appearing beside the first is how they end up opening a second visit
 * the store would refuse anyway (core.md §6, one open per patient).
 */
export const startVisitLabelKey = (row) =>
  (row?.openEncounterId ? 'record.actions.continueVisit' : 'record.actions.startVisit');

/** The reason field's pre-fill: the chief complaint, which is usually right. */
export const defaultVisitReason = (row) => String(row?.chiefComplaint || '').trim();

/**
 * The «هشدار حساسیت» block renders only when the student actually wrote
 * allergies — and then it renders FIRST, above «مشخصات» (core.md §7.4).
 */
export const allergyEntries = (entries) =>
  (entries || []).filter((e) => e?.section === 'allergies');

/** The three most recent patient-level threads, for the overview's last block. */
export const recentThreads = (threads, limit = 3) =>
  [...(threads || [])]
    .filter((thread) => !thread?.encounterId)
    .sort((a, b) => Number(b?.lastMessageAtMs || 0) - Number(a?.lastMessageAtMs || 0))
    .slice(0, limit);

/** Visits, newest first. `seq` is monotonic and never reused, so it orders them. */
export const visitsNewestFirst = (encounters) =>
  [...(encounters || [])].sort((a, b) => Number(b?.seq || 0) - Number(a?.seq || 0));

/** The most recent visit — the one the overview's «آخرین ویزیت» card shows. */
export const latestVisit = (encounters) => visitsNewestFirst(encounters)[0] || null;

/** The first line of a longer field, for a card's excerpt. */
export function firstLine(text, max = 140) {
  const line = String(text || '').split('\n').map((s) => s.trim()).find(Boolean) || '';
  return line.length > max ? `${line.slice(0, max).trimEnd()}…` : line;
}
